import React, { useEffect, useState } from "react";
import axios from "axios";
import backendURL from "../config"; // Assuming this correctly points to your backend base URL

export default function WebsiteConfigurations() {
  // State to hold the *current* configuration values being displayed/edited
  const [websiteAddress, setWebsiteAddress] = useState("");
  const [websiteDescription, setWebsiteDescription] = useState("");
  const [websiteSitemap, setWebsiteSitemap] = useState("");

  // State for feedback messages
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false); // Optional: for loading indication
  const [isSaving, setIsSaving] = useState(false); // Optional: for saving indication

  // Fetch initial data on component mount
  useEffect(() => {
    fetchData();
  }, []); // Empty dependency array ensures this runs only once on mount

  const fetchData = () => {
    setIsLoading(true); // Start loading indication
    setMessage(""); // Clear previous messages
    const token = localStorage.getItem("access_token");
    if (!token) {
        setMessage("Authentication token not found. Please log in.");
        setIsLoading(false);
        return; // Exit if no token
    }
    const headers = { Authorization: `Bearer ${token}` };

    axios
      .get(`${backendURL}/get_website_configuration`, { headers })
      .then((response) => {
        // Assuming the API returns an array of configurations,
        // and we want the latest one (or the first one if only one exists).
        // Adjust logic if your API returns a single object directly.
        const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;

        if (responseData && responseData.length > 0) {
          // Let's assume the *most relevant* config is the last one added
          // Or adjust index if needed (e.g., response.data[0])
          const latestConfig = responseData[responseData.length - 1];
          setWebsiteAddress(latestConfig.website_address || ""); // Use default empty string if null/undefined
          setWebsiteDescription(latestConfig.website_description || "");
          setWebsiteSitemap(latestConfig.website_sitemap || "");
        } else if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
            // Handle case where API returns a single object instead of an array
            setWebsiteAddress(responseData.website_address || "");
            setWebsiteDescription(responseData.website_description || "");
            setWebsiteSitemap(responseData.website_sitemap || "");
        } else {
            // Handle case where data is empty or not in expected format
            console.log("No configuration data found or unexpected format.");
            // Optionally set default values or keep fields empty
            setWebsiteAddress("");
            setWebsiteDescription("");
            setWebsiteSitemap("");
        }
      })
      .catch((error) => {
        error = error.response.data.message || "Error fetching website configurations.";
        console.error(error);
        setMessage(error);
      })
      .finally(() => {
        setIsLoading(false); // Stop loading indication
      });
  };

  const handleSaveButtonClick = () => {
    setIsSaving(true); // Start saving indication
    setMessage(""); // Clear previous messages
    const token = localStorage.getItem("access_token");
     if (!token) {
        setMessage("Authentication token not found. Please log in.");
        setIsSaving(false);
        return; // Exit if no token
    }
    const headers = { Authorization: `Bearer ${token}` };

    // Prepare data to be sent using the current state values
    const data = {
      website_address: websiteAddress,
      website_description: websiteDescription,
      website_sitemap: websiteSitemap,
    };

    axios
      .post(`${backendURL}/update_website_configuration`, data, { headers })
      .then((response) => {
        setMessage(response.data.message || "Configuration saved successfully!");
        console.log("Success:", response.data.message);
        // Optionally re-fetch data to confirm update, though usually not necessary if POST is successful
        // fetchData();
      })
      .catch((error) => {
        console.error("Error saving data:", error);
        let errorMessage = "Error saving configuration."; // Default error
        if (error.response) {
          // Server responded with a status code outside 2xx range
          errorMessage = error.response.data.message || `Server Error: ${error.response.status}`;
          console.error("Error Response Data:", error.response.data);
          console.error("Error Response Status:", error.response.status);
        } else if (error.request) {
          // Request was made but no response received
          errorMessage = "No response from server. Check network connection or backend status.";
          console.error("Error Request:", error.request);
        } else {
          // Something happened in setting up the request
          errorMessage = error.message;
          console.error("Error Message:", error.message);
        }
         setMessage(errorMessage);
      })
      .finally(() => {
        setIsSaving(false); // Stop saving indication
      });
  };

  // Render loading state
  if (isLoading) {
    return (
         <main className="content page-website-configurations">
            <div className="container-fluid p-0">
                <div className="mb-3">
                    <h1 className="h3 d-inline align-middle"> <strong>Website</strong> Configurations</h1>
                </div>
                <p>Loading configurations...</p>
            </div>
         </main>
    );
  }

  return (
    <div>
      <main className="content page-website-configurations">
        <div className="container-fluid p-0">
          <div className="mb-3">
            <h1 className="h3 d-inline align-middle">
              {" "}
              <strong>Website</strong> Configurations
            </h1>
          </div>
          <div className="row">
            <div className="col-12 col-lg-12">
              <div className="card">
                <div className="card-body">
                  {/* Display Message */}
                  {message && (
                    <div className={`alert ${message.toLowerCase().includes("error") ? "alert-danger" : "alert-success"}`} role="alert">
                      {message}
                    </div>
                  )}
                  <div className="row">
                    {/* Website Address Input */}
                    <div className="col-md-4 mb-3"> {/* Use col-md-* for responsiveness */}
                      <h5 className="card-title mb-1">Website Address:</h5>
                      <input
                        type="text"
                        id="wAddress"
                        name="wAddress"
                        className="form-control"
                        value={websiteAddress} // Correctly bound to state
                        onChange={(e) => setWebsiteAddress(e.target.value)} // Updates state on change
                      />
                    </div>
                    {/* Website Description (Title) Input */}
                    <div className="col-md-4 mb-3">
                      <h5 className="card-title mb-1">Title / Description:</h5> {/* Clarified label */}
                      <input
                        type="text"
                        id="wDescription"
                        name="wDescription"
                        className="form-control"
                        value={websiteDescription} // Correctly bound to state
                        onChange={(e) => setWebsiteDescription(e.target.value)} // Updates state on change
                        disabled={isSaving} // Disable input while saving
                      />
                    </div>
                    {/* Website Sitemap Input */}
                    <div className="col-md-4 mb-3">
                      <h5 className="card-title mb-1">Website Sitemap URL:</h5> {/* Clarified label */}
                      <input
                        type="text"
                        id="wSitemap"
                        name="wSitemap"
                        className="form-control"
                        value={websiteSitemap} // Correctly bound to state
                        onChange={(e) => setWebsiteSitemap(e.target.value)} // Updates state on change
                        disabled={isSaving} // Disable input while saving
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 mt-2"> {/* Adjusted margin */}
                      <button
                        className="btn btn-primary"
                        onClick={handleSaveButtonClick}
                        disabled={isSaving} // Disable button while saving
                      >
                        {isSaving ? "Saving..." : "Save Configuration"} {/* Indicate saving state */}
                      </button>
                    </div>
                  </div>
                   {/* Removed the separate message display row as it's now above the inputs */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}